/**
 * Interactive globe of the viewed user's sites.
 *
 * Markers come from the database (locations joined to property_directory),
 * never from a static list. The three.js scene is created lazily (dynamic
 * import) and rebuilt whenever the marker set changes; WebGL is probed
 * first and a plain list is rendered when the browser cannot draw the
 * scene, so the page never depends on a canvas to be usable.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type * as THREE_NS from 'three';
import { useI18n } from '../i18n/I18nProvider';

export interface GlobeMarker {
  id: string;
  code: string;
  name: string;
  lat: number;
  lng: number;
}

const HOME_VIEW = { lat: 39.5, lng: -98 };

function latLngToVector3(lat: number, lng: number, radius: number): [number, number, number] {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);
  return [x, y, z];
}

function rotationToFace(lat: number, lng: number): { x: number; y: number } {
  const [x, y, z] = latLngToVector3(lat, lng, 1);
  return {
    x: Math.asin(Math.max(-1, Math.min(1, y))),
    y: -Math.atan2(x, z),
  };
}

function lerpAngle(current: number, target: number, t: number): number {
  let diff = target - current;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return current + diff * t;
}

function webglAvailable(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export function PropertyGlobe({
  markers,
  selectedId,
  onSelect,
}: {
  markers: GlobeMarker[];
  selectedId: string | null;
  onSelect: (marker: GlobeMarker) => void;
}) {
  const { t } = useI18n();
  const canRender = useMemo(webglAvailable, []);
  const [failed, setFailed] = useState(false);
  const mountRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipCodeRef = useRef<HTMLParagraphElement>(null);
  const tooltipNameRef = useRef<HTMLParagraphElement>(null);
  const onSelectRef = useRef(onSelect);
  const selectedRef = useRef(selectedId);
  const markersRef = useRef(markers);
  const flyToRef = useRef<{ x: number; y: number } | null>(rotationToFace(HOME_VIEW.lat, HOME_VIEW.lng));

  onSelectRef.current = onSelect;
  selectedRef.current = selectedId;
  markersRef.current = markers;

  // Rebuild the scene only when the marker set actually changes.
  const markersKey = markers.map((m) => `${m.id}:${m.lat}:${m.lng}`).join('|');

  useEffect(() => {
    if (selectedId) {
      const marker = markersRef.current.find((m) => m.id === selectedId);
      if (marker) flyToRef.current = rotationToFace(marker.lat, marker.lng);
    }
  }, [selectedId]);

  useEffect(() => {
    if (!canRender) return;
    const container = mountRef.current;
    const labelsRoot = labelsRef.current;
    if (!container) return;

    let disposed = false;
    let animationId = 0;
    const handle: { dispose?: () => void } = {};

    async function initGlobe() {
      try {
        const THREE = await import('three');
        if (disposed || !container) return;

        const width = container.clientWidth || 640;
        const height = container.clientHeight || 480;
        const small = width < 640;
        const segments = small ? 40 : 64;
        const reducedMotion = prefersReducedMotion();

        const scene = new THREE.Scene();
        const mapBg = getComputedStyle(document.documentElement).getPropertyValue('--map-bg').trim() || '#0a0c14';
        scene.background = new THREE.Color(mapBg);

        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
        camera.position.z = 3.05;

        const renderer = new THREE.WebGLRenderer({ antialias: !small, alpha: false, powerPreference: 'low-power' });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, small ? 1.5 : 2));
        container.appendChild(renderer.domElement);

        scene.add(new THREE.AmbientLight(0x334466, 1.2));
        const sunLight = new THREE.DirectionalLight(0xeef4ff, 1.8);
        sunLight.position.set(5, 3, 5);
        scene.add(sunLight);
        scene.add(new THREE.HemisphereLight(0x4488aa, 0x112233, 0.4));

        const globeGroup = new THREE.Group();
        scene.add(globeGroup);
        const start = rotationToFace(HOME_VIEW.lat, HOME_VIEW.lng);
        globeGroup.rotation.x = start.x;
        globeGroup.rotation.y = start.y;

        const disposables: Array<{ dispose: () => void }> = [];
        const textureLoader = new THREE.TextureLoader();
        const loadTexture = (url: string) => {
          const texture = textureLoader.load(url);
          disposables.push(texture);
          return texture;
        };

        const globeRadius = 1;
        const geometry = new THREE.SphereGeometry(globeRadius, segments, segments);
        const earthTexture = loadTexture('https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg');
        earthTexture.colorSpace = THREE.SRGBColorSpace;
        const material = new THREE.MeshPhongMaterial({
          map: earthTexture,
          bumpMap: small ? null : loadTexture('https://unpkg.com/three-globe/example/img/earth-topology.png'),
          bumpScale: 0.015,
          specular: new THREE.Color(0x333333),
          shininess: 15,
        });
        disposables.push(geometry, material);
        globeGroup.add(new THREE.Mesh(geometry, material));

        const atmosGeometry = new THREE.SphereGeometry(1.025, segments, segments);
        const atmosMaterial = new THREE.MeshPhongMaterial({
          color: 0x4488cc,
          transparent: true,
          opacity: 0.1,
          side: THREE.FrontSide,
        });
        disposables.push(atmosGeometry, atmosMaterial);
        globeGroup.add(new THREE.Mesh(atmosGeometry, atmosMaterial));

        const starsGeometry = new THREE.BufferGeometry();
        const starsCount = small ? 800 : 2000;
        const starsPositions = new Float32Array(starsCount * 3);
        for (let i = 0; i < starsCount; i++) {
          starsPositions[i * 3] = (Math.random() - 0.5) * 50;
          starsPositions[i * 3 + 1] = (Math.random() - 0.5) * 50;
          starsPositions[i * 3 + 2] = (Math.random() - 0.5) * 50;
        }
        starsGeometry.setAttribute('position', new THREE.BufferAttribute(starsPositions, 3));
        const starsMaterial = new THREE.PointsMaterial({ color: 0xffffff, size: 0.02, transparent: true, opacity: 0.6 });
        disposables.push(starsGeometry, starsMaterial);
        const stars = new THREE.Points(starsGeometry, starsMaterial);
        scene.add(stars);

        const markerMeshes: THREE_NS.Mesh[] = [];
        const markerHits: THREE_NS.Mesh[] = [];
        const markerPulseRings: THREE_NS.Mesh[] = [];
        const labelEls: HTMLDivElement[] = [];
        const markerLocal = new Map<string, THREE_NS.Vector3>();

        const labelLayer = labelsRoot;
        if (labelLayer) labelLayer.replaceChildren();

        const dotGeometry = new THREE.SphereGeometry(0.022, 16, 16);
        const hitGeometry = new THREE.SphereGeometry(0.06, 12, 12);
        const ringGeometry = new THREE.TorusGeometry(0.03, 0.004, 8, 32);
        const hitMaterial = new THREE.MeshBasicMaterial({ visible: false });
        disposables.push(dotGeometry, hitGeometry, ringGeometry, hitMaterial);

        markersRef.current.forEach((marker) => {
          const [x, y, z] = latLngToVector3(marker.lat, marker.lng, globeRadius);
          markerLocal.set(marker.id, new THREE.Vector3(x, y, z));

          const dotMaterial = new THREE.MeshBasicMaterial({ color: 0x4f98a3 });
          disposables.push(dotMaterial);
          const dot = new THREE.Mesh(dotGeometry, dotMaterial);
          dot.position.set(x, y, z);
          dot.userData = marker;
          globeGroup.add(dot);
          markerMeshes.push(dot);

          const hit = new THREE.Mesh(hitGeometry, hitMaterial);
          hit.position.set(x, y, z);
          hit.userData = marker;
          globeGroup.add(hit);
          markerHits.push(hit);

          const ringMaterial = new THREE.MeshBasicMaterial({ color: 0x4f98a3, transparent: true, opacity: 0.9 });
          disposables.push(ringMaterial);
          const ring = new THREE.Mesh(ringGeometry, ringMaterial);
          ring.position.set(x, y, z);
          ring.lookAt(x * 2, y * 2, z * 2);
          globeGroup.add(ring);
          markerPulseRings.push(ring);

          if (labelLayer) {
            const label = document.createElement('div');
            label.textContent = marker.code;
            label.className =
              'pointer-events-none absolute -translate-x-1/2 -translate-y-full text-[10px] font-semibold tracking-wide';
            label.style.color = '#e8f4f6';
            label.style.textShadow = '0 1px 4px rgba(0,0,0,0.85)';
            label.style.opacity = '0';
            labelLayer.appendChild(label);
            labelEls.push(label);
          }
        });

        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        let isDragging = false;
        let moved = false;
        const previousMouse = { x: 0, y: 0 };
        const rotationVelocity = { x: 0, y: 0 };
        const idleSpeed = reducedMotion ? 0 : 0.0007;
        let autoRotateSpeed = idleSpeed;

        const pointerToNdc = (clientX: number, clientY: number) => {
          const rect = container.getBoundingClientRect();
          mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
          mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;
          return rect;
        };

        const onPointerDown = (e: PointerEvent) => {
          isDragging = true;
          moved = false;
          previousMouse.x = e.clientX;
          previousMouse.y = e.clientY;
          autoRotateSpeed = 0;
          flyToRef.current = null;
        };

        const onPointerMove = (e: PointerEvent) => {
          const rect = pointerToNdc(e.clientX, e.clientY);
          if (isDragging) {
            const deltaX = e.clientX - previousMouse.x;
            const deltaY = e.clientY - previousMouse.y;
            if (Math.abs(deltaX) + Math.abs(deltaY) > 3) moved = true;
            rotationVelocity.x = deltaY * 0.003;
            rotationVelocity.y = deltaX * 0.003;
            globeGroup.rotation.y += deltaX * 0.005;
            globeGroup.rotation.x += deltaY * 0.005;
            globeGroup.rotation.x = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, globeGroup.rotation.x));
            previousMouse.x = e.clientX;
            previousMouse.y = e.clientY;
          }

          raycaster.setFromCamera(mouse, camera);
          const intersects = raycaster.intersectObjects(markerHits);
          const tooltip = tooltipRef.current;
          if (intersects.length > 0) {
            const marker = intersects[0].object.userData as GlobeMarker;
            container.style.cursor = 'pointer';
            if (tooltip) {
              tooltip.style.display = 'block';
              tooltip.style.left = `${e.clientX - rect.left}px`;
              tooltip.style.top = `${e.clientY - rect.top - 10}px`;
              if (tooltipCodeRef.current) tooltipCodeRef.current.textContent = marker.code;
              if (tooltipNameRef.current) tooltipNameRef.current.textContent = marker.name;
            }
          } else {
            container.style.cursor = isDragging ? 'grabbing' : 'grab';
            if (tooltip) tooltip.style.display = 'none';
          }
        };

        const onPointerUp = () => {
          isDragging = false;
          window.setTimeout(() => {
            if (!selectedRef.current) autoRotateSpeed = idleSpeed;
          }, 2000);
        };

        const onClick = (e: MouseEvent) => {
          if (moved) return;
          pointerToNdc(e.clientX, e.clientY);
          raycaster.setFromCamera(mouse, camera);
          const intersects = raycaster.intersectObjects(markerHits);
          if (intersects.length > 0) {
            const marker = intersects[0].object.userData as GlobeMarker;
            flyToRef.current = rotationToFace(marker.lat, marker.lng);
            autoRotateSpeed = 0;
            onSelectRef.current(marker);
          }
        };

        const onResize = () => {
          const w = container.clientWidth;
          const h = container.clientHeight;
          if (!w || !h) return;
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h);
        };

        container.addEventListener('pointerdown', onPointerDown);
        container.addEventListener('pointermove', onPointerMove);
        container.addEventListener('pointerup', onPointerUp);
        container.addEventListener('pointerleave', onPointerUp);
        container.addEventListener('click', onClick);
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
        resizeObserver?.observe(container);
        window.addEventListener('resize', onResize);

        const clock = new THREE.Clock();
        const projected = new THREE.Vector3();
        const camDir = new THREE.Vector3();

        const animate = () => {
          if (disposed) return;
          animationId = requestAnimationFrame(animate);
          const elapsed = clock.getElapsedTime();

          const flyTo = flyToRef.current;
          if (flyTo) {
            globeGroup.rotation.y = lerpAngle(globeGroup.rotation.y, flyTo.y, 0.08);
            globeGroup.rotation.x += (flyTo.x - globeGroup.rotation.x) * 0.08;
            autoRotateSpeed = 0;
          } else {
            globeGroup.rotation.y += autoRotateSpeed;
            if (!isDragging) {
              rotationVelocity.x *= 0.95;
              rotationVelocity.y *= 0.95;
              globeGroup.rotation.x += rotationVelocity.x;
              globeGroup.rotation.y += rotationVelocity.y;
              globeGroup.rotation.x = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, globeGroup.rotation.x));
            }
          }

          markerPulseRings.forEach((ring, i) => {
            const pulseFactor = reducedMotion ? 0.5 : (Math.sin(elapsed * 2 + i * 0.8) + 1) / 2;
            const scale = 1 + pulseFactor * 2;
            ring.scale.set(scale, scale, scale);
            (ring.material as THREE_NS.MeshBasicMaterial).opacity = 0.9 * (1 - pulseFactor * 0.8);
          });

          const selected = selectedRef.current;
          markerMeshes.forEach((dot, i) => {
            const marker = dot.userData as GlobeMarker;
            const isSelected = marker.id === selected;
            const glow = reducedMotion ? 1 : 0.85 + Math.sin(elapsed * 3 + i * 0.5) * 0.15;
            const mat = dot.material as THREE_NS.MeshBasicMaterial;
            if (isSelected) {
              mat.color.setRGB(0.95 * glow, 0.72 * glow, 0.22 * glow);
              dot.scale.setScalar(1.45);
            } else {
              mat.color.setRGB(0.31 * glow, 0.6 * glow, 0.64 * glow);
              dot.scale.setScalar(1);
            }
          });

          if (!reducedMotion) stars.rotation.y += 0.00005;

          globeGroup.updateMatrixWorld();
          camera.getWorldPosition(camDir);
          camDir.normalize();
          markersRef.current.forEach((marker, i) => {
            const label = labelEls[i];
            const local = markerLocal.get(marker.id);
            if (!label || !local) return;
            if (marker.id !== selected) {
              label.style.opacity = '0';
              return;
            }
            projected.copy(local).applyMatrix4(globeGroup.matrixWorld);
            const facing = projected.dot(camDir) > 0.12;
            projected.project(camera);
            const onscreen =
              facing && projected.x >= -1.05 && projected.x <= 1.05 && projected.y >= -1.05 && projected.y <= 1.05;
            if (!onscreen) {
              label.style.opacity = '0';
              return;
            }
            const lx = (projected.x * 0.5 + 0.5) * container.clientWidth;
            const ly = (-projected.y * 0.5 + 0.5) * container.clientHeight;
            label.style.transform = `translate(-50%, -120%) translate(${lx}px, ${ly}px)`;
            label.style.opacity = '1';
            label.style.color = '#E8AF34';
          });

          renderer.render(scene, camera);
        };

        animate();

        handle.dispose = () => {
          cancelAnimationFrame(animationId);
          container.removeEventListener('pointerdown', onPointerDown);
          container.removeEventListener('pointermove', onPointerMove);
          container.removeEventListener('pointerup', onPointerUp);
          container.removeEventListener('pointerleave', onPointerUp);
          container.removeEventListener('click', onClick);
          resizeObserver?.disconnect();
          window.removeEventListener('resize', onResize);
          disposables.forEach((d) => d.dispose());
          renderer.dispose();
          renderer.forceContextLoss();
          if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
          if (labelLayer) labelLayer.replaceChildren();
        };
        if (disposed) handle.dispose();
      } catch (err) {
        console.error('Globe init error:', err);
        if (!disposed) setFailed(true);
      }
    }

    void initGlobe();
    return () => {
      disposed = true;
      handle.dispose?.();
    };
  }, [canRender, markersKey]);

  if (!canRender || failed) {
    return (
      <div className="flex h-full w-full flex-col rounded-lg border border-border bg-surface">
        <div className="border-b border-border px-4 py-3">
          <p className="text-sm font-medium text-text">
            {failed ? t('locations.map_error') : t('locations.map_unavailable')}
          </p>
          <p className="text-xs text-text-muted">{t('locations.map_unavailable_hint')}</p>
        </div>
        <ul className="min-h-0 flex-1 divide-y divide-divider overflow-y-auto" aria-label={t('locations.map_title')}>
          {markers.map((marker) => (
            <li key={marker.id}>
              <button
                type="button"
                onClick={() => onSelect(marker)}
                aria-pressed={marker.id === selectedId}
                className={`flex min-h-11 w-full items-center gap-3 px-4 text-left text-sm hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
                  marker.id === selectedId ? 'bg-surface-3' : ''
                }`}
              >
                <span className="w-12 flex-shrink-0 font-mono text-xs text-primary">{marker.code}</span>
                <span className="truncate text-text">{marker.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full" style={{ background: 'var(--map-bg, #0a0c14)' }}>
      <div
        ref={mountRef}
        className="absolute inset-0"
        style={{ cursor: 'grab', touchAction: 'none' }}
        role="img"
        aria-label={t('locations.map_title')}
      />
      <div ref={labelsRef} className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" />
      <div
        ref={tooltipRef}
        className="pointer-events-none absolute z-20 min-w-[180px] -translate-x-1/2 -translate-y-full rounded-md px-3 py-2"
        aria-hidden="true"
        style={{
          display: 'none',
          background: 'rgba(27,29,39,0.95)',
          border: '1px solid rgba(79,152,163,0.35)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <p ref={tooltipCodeRef} className="mb-0.5 text-[10px] uppercase tracking-widest text-primary" />
        <p ref={tooltipNameRef} className="text-xs font-medium text-white" />
      </div>
    </div>
  );
}
